import Check from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { City, Professional, Team } from "@syadem/ariane-js";
import {
  CreateVaccinationActBodyVaccinationActVaccinator,
  HealthRecord,
  ModelError,
  ResponseError,
  VaccinationAct,
  VaccinationActInjectionLocationEnum,
  VaccinationActInjectionMethodEnum,
} from "@syadem/kairos-citizen-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Country } from "../../../domain/country";
import { certificationDescription } from "../../../utils/certificationDescription";
import { dateErrorTranslation, formatDateForApi } from "../../../utils/date";
import { dayjs, localizedPlaceholder } from "../../../utils/dayjs";
import { compareNormalizedString } from "../../../utils/string";
import { certificationAdditionalInfo } from "../../../utils/vaccinationActs";
import DatamatrixButton from "../../components/Datamatrix";
import FormVaccinesAutocomplete from "../../components/FormVaccinesAutocomplete";
import { StyledAutocomplete } from "../../components/mui/StyledAutoComplete";
import { CancelButton, CancelLinkButton } from "../../components/mui/StyledButtons";
import StyledDatepicker from "../../components/mui/StyledDatepicker";
import { StyledInput } from "../../components/mui/StyledInput";
import { StyledSelect } from "../../components/mui/StyledSelect";
import { ListItemDeleteButton } from "../../components/shared/ListItemDeleteButton";
import { BoosterForm } from "../../components/vaccination/BoosterForm";
import { useAuthenticatedUser } from "../../hooks";
import { useArianeApi } from "../../hooks/useArianeApi";
import { useCountryConfig } from "../../hooks/useCountryConfig";
import { useI18n } from "../../hooks/useI18n";
import { useKairosApi } from "../../hooks/useKairosApi";
import { useServiceBus } from "../../hooks/useServiceBus";
import { theme } from "../../layout/Theme";
import AddSameVaccineDialog from "../../pages/health_record/AddSameVaccineDialog";
import AutoCloseAlert from "../AutoCloseAlert";
import { StyledAccordion } from "../mui/StyledAccordion";
import { CitySearchField } from "../sharing/CitySearchField";
import { ProfessionalSearchField } from "../sharing/ProfessionalSearchField";
import { isCertified } from "../VaccinationActListItem";

interface VaccinationActFormFieldValues {
  performedOn: Date | null;
  vaccineId: string;
  batchNumber: string;
  serialNumber: string;
  injectionLocation: VaccinationActInjectionLocationEnum | "";
  injectionMethod: VaccinationActInjectionMethodEnum | "";
  expirationDate?: Date;
  booster: boolean;
  countryCode: string;
  vaccinator: CreateVaccinationActBodyVaccinationActVaccinator | null;
}

function getVaccinator(
  firstName: string,
  lastName: string,
  externalId: string | null,
): CreateVaccinationActBodyVaccinationActVaccinator | null {
  if (firstName.length === 0 || lastName.length === 0) return null;

  return {
    firstName,
    lastName,
    externalId,
  };
}

export const VaccinationActForm = ({
  isEmbedded = false,
  refreshCallback,
  setSnackOpen,
  handleClose,
  isDatamatrixOpen,
  healthRecord,
  vaccinationAct,
}: {
  isEmbedded?: boolean;
  refreshCallback?: () => void;
  setSnackOpen?: (value: React.SetStateAction<boolean>) => void;
  handleClose?: () => void;
  isDatamatrixOpen?: boolean;
  healthRecord: HealthRecord;
  vaccinationAct?: VaccinationAct;
}) => {
  const { t, locale, getObject } = useI18n();
  const currentUser = useAuthenticatedUser();
  const [isReadonly, setIsReadonly] = useState<boolean>(false);
  const [certificator, setCertificator] = useState<Professional | Team | undefined>(undefined);
  const [openSameVaccinationDialog, setOpenSameVaccinationDialog] = useState(false);
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  // Submission
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const api = useKairosApi();
  const serviceBus = useServiceBus();
  const arianeApi = useArianeApi();
  const [showAdditionalInfos, setShowAdditionalInfos] = useState(false);
  const smallResolution = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (vaccinationAct) {
      setIsReadonly(isCertified(vaccinationAct.certificationMethod));
      setShowAdditionalInfos(true);
    }
  }, [vaccinationAct, currentUser]);

  useEffect(() => {
    if (vaccinationAct && vaccinationAct.certificator) {
      arianeApi.professionals
        .searchByIds([vaccinationAct.certificator.id])
        .then((result) => setCertificator(result[0]))
        .catch((_error) => setCertificator(undefined));
    } else {
      setCertificator(undefined);
    }
  }, [arianeApi.professionals, vaccinationAct]);

  // Form state
  const { defaultCountryCode } = useCountryConfig();
  const [performedOn, setPerformedOn] = useState<Date | null>(
    vaccinationAct?.performedOn
      ? dayjs.utc(vaccinationAct?.performedOn).toDate()
      : dayjs().startOf("day").utc().toDate(),
  );
  const [expirationDate, setExpirationDate] = useState(
    vaccinationAct?.expirationDate ? dayjs.utc(vaccinationAct?.expirationDate).toDate() : undefined,
  );
  const [vaccineId, setVaccineId] = useState(vaccinationAct?.vaccineId || "");
  const [countryCode, setCountryCode] = useState(vaccinationAct?.countryCode || defaultCountryCode);
  const [countryCodeInput, setCountryCodeInput] = useState<string | undefined>(undefined);
  const [batchNumber, setBatchNumber] = useState(vaccinationAct?.batchNumber || "");
  const [serialNumber, setSerialNumber] = useState(vaccinationAct?.serialNumber || "");
  const [vaccinatorFirstName, setVaccinatorFirstName] = useState(vaccinationAct?.vaccinator?.firstName || "");
  const [vaccinatorLastName, setVaccinatorLastName] = useState(vaccinationAct?.vaccinator?.lastName || "");
  const [vaccinatorExternalId, setVaccinatorExternalId] = useState(vaccinationAct?.vaccinator?.externalId ?? null);
  const [vaccinatorNamesError, setVaccinatorNamesError] = useState(false);
  const [vaccinatorDialogOpen, setVaccinatorDialogOpen] = useState<boolean>(false);
  const [vaccinator, setVaccinator] = useState<Professional | undefined>(undefined);
  const [vaccinatorCity, setVaccinatorCity] = useState<City | undefined>(undefined);
  const [injectionLocation, setInjectionLocation] = useState<VaccinationActInjectionLocationEnum | "">(
    vaccinationAct?.injectionLocation || "",
  );
  const [injectionMethod, setInjectionMethod] = useState<VaccinationActInjectionMethodEnum | "">(
    vaccinationAct?.injectionMethod || "",
  );
  const [booster, setBooster] = useState<boolean>(vaccinationAct?.booster ?? false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const performedErrorMessage = useMemo(() => {
    const max = dayjs().endOf("day").toDate();
    return dateErrorTranslation(t, performedOn, { min: healthRecord.birthDate, max });
  }, [t, performedOn, healthRecord.birthDate]);

  const expirationDateErrorMessage = useMemo(() => {
    return dateErrorTranslation(t, expirationDate ?? null);
  }, [t, expirationDate]);

  const countries = useMemo(() => {
    return Object.entries(getObject("countries")).reduce(
      (acc: Country[], [key, value]) => [...acc, { code: key, name: value }],
      [],
    );
  }, [getObject]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (performedOn === null) return;

      const hasVaccinatorFirstName = vaccinatorFirstName.length > 0;
      const hasVaccinatorLastName = vaccinatorLastName.length > 0;

      if (hasVaccinatorFirstName !== hasVaccinatorLastName) {
        setVaccinatorNamesError(true);
        return;
      }

      setIsSubmitting(true);
      setErrorMessage(undefined);

      const values = {
        performedOn,
        vaccineId,
        countryCode,
        batchNumber,
        serialNumber,
        injectionLocation: injectionLocation as VaccinationActInjectionLocationEnum,
        injectionMethod: injectionMethod as VaccinationActInjectionMethodEnum,
        booster,
        expirationDate,
        vaccinator: getVaccinator(vaccinatorFirstName, vaccinatorLastName, vaccinatorExternalId),
      };

      try {
        if (vaccinationAct) {
          const result = await serviceBus.dispatch({
            type: "updateVaccinationAct",
            healthRecordId: healthRecord.id,
            vaccinationAct,
            updatedVaccinationAct: {
              ...values,
              ...(values.expirationDate ? { expirationDate: formatDateForApi(values.expirationDate) } : {}),
              performedOn: formatDateForApi(values.performedOn),
            },
          });

          if (result.type === "error") return;

          navigate(`..?updated=true`);
          if (setSnackOpen) setSnackOpen(true);
        } else {
          const vaccinationActs = healthRecord?.vaccinationActs || [];

          const existingVaccinationAct = vaccinationActs.find((vaccinationAct) => {
            return (
              dayjs(vaccinationAct.performedOn).isSame(values.performedOn, "day") &&
              vaccinationAct.vaccineId === values.vaccineId
            );
          });

          if (existingVaccinationAct) {
            setOpenSameVaccinationDialog(true);
          } else {
            const result = await serviceBus.dispatch({
              type: "createVaccinationAct",
              healthRecordId: healthRecord.id,
              vaccinationAct: {
                ...values,
                ...(values.expirationDate ? { expirationDate: formatDateForApi(values.expirationDate) } : {}),
                performedOn: formatDateForApi(values.performedOn),
              },
            });

            if (result.type === "error") return;

            if (!isEmbedded) {
              navigate(`..?updated=true`);
            } else {
              if (refreshCallback) refreshCallback();
              setAlertIsOpen(true);
            }
          }
        }
      } catch (e) {
        if ((e instanceof Response && e.status === 400) || (e instanceof ResponseError && e.response.status === 400)) {
          const response = e instanceof Response ? e : e.response;
          const code = ((await response.json()) as ModelError).code;

          switch (code) {
            case "act_cannot_be_performed_in_the_future_or_before_patient_birth_date":
              setErrorMessage(t("vaccines.error_messages.error_1"));
              break;
            case "act_cannot_be_performed_after_vaccine_expiration_date":
              setErrorMessage(t("vaccines.error_messages.error_2"));
              break;
            case "incorrect_injection_method":
              setErrorMessage(t("vaccines.error_messages.error_3"));
              break;
            case "incorrect_injection_location":
              setErrorMessage(t("vaccines.error_messages.error_4"));
              break;
          }
        } else {
          setErrorMessage(t("common.alerts.alert_notification"));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      batchNumber,
      booster,
      countryCode,
      expirationDate,
      healthRecord,
      injectionLocation,
      injectionMethod,
      isEmbedded,
      navigate,
      performedOn,
      refreshCallback,
      serialNumber,
      serviceBus,
      setSnackOpen,
      t,
      vaccinationAct,
      vaccinatorExternalId,
      vaccinatorFirstName,
      vaccinatorLastName,
      vaccineId,
    ],
  );

  const handleDatamatrixScan = (scanData: {
    vaccineId: string;
    expirationDate?: Date;
    batchNumber: string;
    serialNumber: string;
  }) => {
    setVaccineId(scanData.vaccineId);
    setExpirationDate(scanData.expirationDate);
    setBatchNumber(scanData.batchNumber);
    setSerialNumber(scanData.serialNumber);
  };

  return (
    <>
      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <Input
          type="text"
          id="PreventChromeAutocomplete"
          name="PreventChromeAutocomplete"
          autoComplete="address-level4"
          sx={{ display: "none" }}
        />
        <Grid container rowSpacing={2} columnSpacing={3} justifyContent="space-between">
          <Grid size={12} container justifyContent="space-between">
            <Grid>
              <Typography variant="h6" marginBottom={4}>
                {vaccinationAct
                  ? isReadonly
                    ? t("vaccines.view_vaccination")
                    : t("vaccines.edit_vaccination")
                  : t("vaccines.add_vaccination")}
              </Typography>
            </Grid>
            {!isReadonly ? (
              <Grid>
                <DatamatrixButton onScanSuccess={handleDatamatrixScan} isOpen={isDatamatrixOpen} />
              </Grid>
            ) : null}
          </Grid>
          {errorMessage ? (
            <Grid size={12}>
              <Alert severity="error">{errorMessage}</Alert>
            </Grid>
          ) : null}
          <Grid container size={12} columnSpacing={2} rowSpacing={2}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <StyledDatepicker
                placeholder={localizedPlaceholder(locale, t)}
                label={t("vaccines.date_act")}
                required
                readOnly={isReadonly}
                fullWidth
                value={performedOn}
                name="performedOn"
                minDate={healthRecord?.birthDate}
                maxDate={new Date()}
                onChange={(date) => setPerformedOn(date ?? null)}
                error={!!performedErrorMessage}
                errorMessage={performedErrorMessage}
                warningText={
                  performedOn &&
                  expirationDate &&
                  dayjs(performedOn).isValid() &&
                  dayjs(expirationDate).isValid() &&
                  dayjs(performedOn).isAfter(dayjs(expirationDate))
                    ? t("vaccines.act_date_greater_than_expiration_date")
                    : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 8 }}>
              <FormVaccinesAutocomplete
                readOnly={isReadonly}
                vaccineId={vaccineId}
                onVaccineChange={setVaccineId}
                performedOn={performedOn}
              />
              <BoosterForm
                isReadonly={isReadonly}
                booster={booster}
                onBoosterChange={setBooster}
                vaccinationDate={performedOn ?? undefined}
                vaccineId={vaccineId}
                birthDate={healthRecord?.birthDate}
              />
            </Grid>
          </Grid>
          <Grid size={12}>
            <StyledAccordion
              expanded={showAdditionalInfos}
              onChange={() => setShowAdditionalInfos((prevState) => !prevState)}
            >
              <AccordionSummary id="panel-header" aria-controls="panel-content" expandIcon={<ExpandMoreIcon />}>
                <Typography
                  sx={{
                    color: theme.palette.neutral[600],
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: ["Open Sans", "sans-serif"].join(","),
                  }}
                >
                  {t("vaccines.additional_information")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                  <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledInput
                        id="batchInput"
                        type="text"
                        name="batchNumber"
                        value={batchNumber}
                        onChange={(event) => setBatchNumber(event.target.value)}
                        label={t("vaccines.batch_number")}
                        readOnly={isReadonly}
                        fullWidth
                        placeholder={!isReadonly ? "ex : 3455673452372737" : ""}
                        autoComplete="cc-csc" // hack to not have credit card number validation message from Chrome because the label contains the word "Number"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }} sx={{ visibility: "hidden" }}>
                      <StyledInput
                        id="serialInput"
                        type="text"
                        name="serialNumber"
                        value={serialNumber}
                        onChange={(event) => setSerialNumber(event.target.value)}
                        label={t("vaccines.serial_number")}
                        readOnly={isReadonly}
                        fullWidth
                        placeholder={!isReadonly ? "ex : 27482783002389" : ""}
                        autoComplete="cc-csc" // hack to not have credit card number validation message from Chrome because the label contains the word "Number"
                      />
                    </Grid>
                  </Grid>
                  <Grid size={12} container columnSpacing={3} rowSpacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledSelect<VaccinationActInjectionMethodEnum | "">
                        label={t("vaccines.injection_path")}
                        name="injectionMethod"
                        value={injectionMethod}
                        onChange={(event) => setInjectionMethod(event.target.value)}
                        fullWidth
                        placeholder={!isReadonly ? `ex : ${t("injectionMethods.intramuscular")}` : ""}
                        renderValue={(injectionMethod) =>
                          injectionMethod !== VaccinationActInjectionMethodEnum.Null
                            ? t(`injectionMethods.${injectionMethod}`)
                            : ""
                        }
                        readOnly={isReadonly}
                      >
                        {Object.values(VaccinationActInjectionMethodEnum)
                          .filter((injectionMethod) => injectionMethod != VaccinationActInjectionMethodEnum.Null)
                          .map((filteredInjectionMethod) => (
                            <MenuItem key={filteredInjectionMethod} value={filteredInjectionMethod}>
                              {t(`injectionMethods.${filteredInjectionMethod}`)}
                            </MenuItem>
                          ))}
                      </StyledSelect>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <StyledSelect<VaccinationActInjectionLocationEnum | "">
                        label={t("vaccines.injection_location")}
                        name="injectionLocation"
                        value={injectionLocation}
                        onChange={(event) => setInjectionLocation(event.target.value)}
                        fullWidth
                        placeholder={!isReadonly ? `ex : ${t("injectionLocations.left_deltoid")}` : ""}
                        renderValue={(injectionLocation) =>
                          injectionLocation !== VaccinationActInjectionLocationEnum.Null
                            ? t(`injectionLocations.${injectionLocation}`)
                            : ""
                        }
                        readOnly={isReadonly}
                      >
                        {Object.values(VaccinationActInjectionLocationEnum)
                          .filter((injectionLocation) => injectionLocation != VaccinationActInjectionLocationEnum.Null)
                          .map((filteredInjectionLocation) => (
                            <MenuItem key={filteredInjectionLocation} value={filteredInjectionLocation}>
                              {t(`injectionLocations.${filteredInjectionLocation}`)}
                            </MenuItem>
                          ))}
                      </StyledSelect>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StyledDatepicker
                      name="expirationDate"
                      value={expirationDate ?? null}
                      placeholder={!isReadonly ? localizedPlaceholder(locale, t) : ""}
                      label={t("vaccines.expiration_date")}
                      onChange={(date) => setExpirationDate(date)}
                      readOnly={isReadonly}
                      fullWidth
                      error={!!expirationDateErrorMessage}
                      errorMessage={expirationDateErrorMessage}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StyledAutocomplete
                      id="countryCode"
                      label={t("common.user_infos.country")}
                      fullWidth
                      required
                      readOnly={isReadonly}
                      value={countries.find((country) => country.code == countryCode) || null}
                      options={countries}
                      filterOptions={(x) => x.filter((e) => compareNormalizedString(e.name, countryCodeInput || ""))}
                      getOptionLabel={(country) => country.name || ""}
                      onInputChange={(_event, newCountryCode, reason) => {
                        if (reason === "input") {
                          setCountryCodeInput(newCountryCode);
                        }
                      }}
                      onChange={(_event, newCountryCode) => {
                        if (!newCountryCode) {
                          setCountryCodeInput(undefined);
                          setCountryCode("");
                        } else {
                          setCountryCode(newCountryCode.code);
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={12} container rowSpacing={0} mt={isReadonly ? "default" : -0.8}>
                    <Grid
                      size={12}
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      mb={isReadonly ? 0.5 : "default"}
                    >
                      <InputLabel
                        shrink
                        sx={{
                          color: theme.palette.neutral[600],
                          fontSize: 14,
                          fontWeight: 600,
                          fontFamily: ["Open Sans", "sans-serif"].join(","),
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          transition: "color 0.2s ease-in-out",
                          transform: "none",
                          "&.Mui-focused": {
                            color: isReadonly ? theme.palette.neutral[600] : theme.palette.primary.main,
                          },
                        }}
                      >
                        {t("vaccinator.vaccinator")}
                      </InputLabel>
                      {isReadonly ? null : (
                        <Button
                          size="small"
                          startIcon={<SearchIcon fontSize="small" />}
                          onClick={() => setVaccinatorDialogOpen(true)}
                        >
                          {smallResolution ? t("vaccinator.search") : t("vaccinator.searchInDirectory")}
                        </Button>
                      )}
                    </Grid>
                    <Grid size={6}>
                      <StyledInput
                        name="vaccinatorFirstName"
                        value={vaccinatorFirstName}
                        onChange={(event) => {
                          const firstName = event.target.value;

                          setVaccinatorFirstName(firstName);
                          setVaccinator(undefined);
                          setVaccinatorExternalId(null);

                          if (firstName.length > 0 === vaccinatorLastName.length > 0) {
                            setVaccinatorNamesError(false);
                          }
                        }}
                        error={vaccinatorNamesError && vaccinatorFirstName.length === 0}
                        readOnly={isReadonly}
                        fullWidth
                        placeholder={!isReadonly ? t("vaccinator.firstName") : ""}
                      />
                    </Grid>
                    <Grid size={6}>
                      <StyledInput
                        name="vaccinatorLastName"
                        value={vaccinatorLastName}
                        onChange={(event) => {
                          const lastName = event.target.value;

                          setVaccinatorLastName(lastName);
                          setVaccinator(undefined);
                          setVaccinatorExternalId(null);

                          if (vaccinatorFirstName.length > 0 === lastName.length > 0) {
                            setVaccinatorNamesError(false);
                          }
                        }}
                        error={vaccinatorNamesError && vaccinatorLastName.length === 0}
                        readOnly={isReadonly}
                        fullWidth
                        placeholder={!isReadonly ? t("vaccinator.lastName") : ""}
                      />
                    </Grid>
                    {vaccinatorNamesError ? (
                      <Grid size={12}>
                        <FormHelperText error>{t("vaccinator.namesRequired")}</FormHelperText>
                      </Grid>
                    ) : null}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </StyledAccordion>
          </Grid>
          {vaccinationAct && isReadonly ? (
            <Grid size={12}>
              <Typography variant="body2">
                {certificationDescription(certificator, vaccinationAct, t, locale)}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", fontStyle: "italic", color: "#808080" }}>
                {certificationAdditionalInfo(vaccinationAct, t)}
              </Typography>
            </Grid>
          ) : null}
          <Grid size={12} container justifyContent="space-between" marginTop="30px">
            <Box sx={{ display: "inlinde-flex" }}>
              {isEmbedded ? (
                <CancelButton onClick={handleClose} sx={{ mr: 2 }}>
                  {t("common.cta.close")}
                </CancelButton>
              ) : (
                <CancelLinkButton
                  component={Link}
                  to={`${pathname.substring(0, pathname.lastIndexOf("/health-records") + 15)}/${healthRecord.id}`}
                  variant="contained"
                  disableElevation
                >
                  {t("common.cta.cancel")}
                </CancelLinkButton>
              )}
              {!isReadonly && api && vaccinationAct ? (
                <ListItemDeleteButton
                  deleteRecord={() =>
                    api.vaccinationActApi.deleteVaccinationAct(
                      vaccinationAct.healthRecordId as string,
                      vaccinationAct.id,
                    )
                  }
                  refreshCallback={() => {
                    navigate(`/health-records/${healthRecord.id}`);
                  }}
                  title={t("common.cta.delete")}
                  modalTitle={t("vaccines.delete_vaccination")}
                  modalContent={t("vaccines.delete_vaccination_validation")}
                  icon={<DeleteOutlineIcon color="error" />}
                  sx={{ ml: 2 }}
                  variant="outlined"
                  buttonWidth="110px"
                />
              ) : null}
              <AutoCloseAlert
                icon={<Check fontSize="small" />}
                severity="success"
                open={alertIsOpen}
                handleClose={() => setAlertIsOpen(false)}
              >
                {t("vaccines.addVaccinationConfirmed")}
              </AutoCloseAlert>
            </Box>
            {!isReadonly ? (
              <Button variant="contained" disableElevation type="submit" loading={isSubmitting} data-testid="submit">
                {vaccinationAct ? t("common.cta.save") : t("common.cta.add")}
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </form>
      <AddSameVaccinationDialog
        healthRecordId={healthRecord.id}
        refreshCallback={refreshCallback}
        open={openSameVaccinationDialog}
        onClose={() => setOpenSameVaccinationDialog(false)}
        openAlert={() => setAlertIsOpen(true)}
        formValues={{
          performedOn,
          vaccineId,
          countryCode,
          batchNumber,
          serialNumber,
          injectionLocation,
          injectionMethod,
          booster,
          expirationDate,
          vaccinator: getVaccinator(vaccinatorFirstName, vaccinatorLastName, vaccinatorExternalId),
        }}
      />
      <Dialog open={vaccinatorDialogOpen} onClose={() => setVaccinatorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("vaccinator.vaccinator")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography>{t("vaccinator.searchGuidance")}</Typography>
          <CitySearchField
            city={vaccinatorCity}
            onCityChange={(city) => {
              setVaccinatorCity(city);
              setVaccinator(undefined);
            }}
          />
          <ProfessionalSearchField
            city={vaccinatorCity}
            professional={vaccinator}
            professionalsOnly
            onProfessionalChange={setVaccinator}
          />
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Button onClick={() => setVaccinatorDialogOpen(false)} variant="outlined">
            {t("common.cta.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (vaccinator && vaccinator.first_name && vaccinator.last_name) {
                setVaccinatorFirstName(vaccinator.first_name);
                setVaccinatorLastName(vaccinator.last_name);
                setVaccinatorExternalId(vaccinator.rpps ?? null);
                setVaccinatorNamesError(false);
                setVaccinatorDialogOpen(false);
              }
            }}
          >
            {t("common.cta.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AddSameVaccinationDialog = ({
  healthRecordId,
  open,
  onClose,
  openAlert,
  formValues,
  refreshCallback,
}: {
  healthRecordId: string;
  open: boolean;
  onClose: () => void;
  openAlert: () => void;
  formValues: VaccinationActFormFieldValues;
  refreshCallback?: () => void;
}) => {
  const serviceBus = useServiceBus();
  const [isCreating, setIsCreating] = useState(false);

  const createVaccine = useCallback(async () => {
    if (formValues.performedOn === null) return;
    setIsCreating(true);

    const result = await serviceBus.dispatch({
      type: "createVaccinationAct",
      healthRecordId,
      vaccinationAct: {
        ...formValues,
        ...(formValues.expirationDate ? { expirationDate: formatDateForApi(formValues.expirationDate) } : {}),
        performedOn: formatDateForApi(formValues.performedOn),
      },
    });

    setIsCreating(false);

    if (result.type === "success") {
      if (refreshCallback) refreshCallback();
      onClose();
      openAlert();
    }
  }, [formValues, healthRecordId, onClose, openAlert, refreshCallback, serviceBus]);

  return <AddSameVaccineDialog open={open} onClose={onClose} createVaccine={createVaccine} isCreating={isCreating} />;
};
